import { Box, Button, Typography } from '@material-ui/core';
import HealingIcon from '@material-ui/icons/Healing';
import CallMergeIcon from '@material-ui/icons/CallMerge';
import VisibilityIcon from '@material-ui/icons/Visibility';
import type { TestRun } from '../../interfaces/ui';
import TestExecutionActionDropdown from '../../common/TestExecutionActionDropdown';
import type {
  TestExecutionActionKey,
  TestExecutionActionOption,
} from '../../utils/projectExecutionLifecycle';

interface TestRunSummaryProps {
  currentVersion: TestRun | null;
  actionOptions: TestExecutionActionOption[];
  actionDisabled: boolean;
  isExecutionInProgress?: boolean;
  onViewCurrentStatus?: () => void;
  onActionSelect: (action: TestExecutionActionKey) => void;
  showActions?: boolean;
}

export default function TestRunSummary({
  currentVersion,
  actionOptions,
  actionDisabled,
  isExecutionInProgress,
  onViewCurrentStatus,
  onActionSelect,
  showActions = true,
}: TestRunSummaryProps) {
  if (!currentVersion) return null;

  return (
    <Box>
      <Box className="pd-summary-header">
        <Typography
          variant="body1"
          className="pd-section-title pd-summary-title"
        >
          Latest run summary
        </Typography>
        {showActions && (
          <Box className="pd-run-actions">
            {isExecutionInProgress ? (
              <Button
                variant="contained"
                color="primary"
                className="pd-run-action-btn"
                startIcon={<VisibilityIcon />}
                onClick={onViewCurrentStatus}
              >
                View Current Status
              </Button>
            ) : (
              <TestExecutionActionDropdown
                buttonClassName="pc-btn-tinted pc-action-dropdown-trigger"
                menuClassName="pc-action-dropdown-menu"
                menuItemClassName="pc-action-dropdown-item"
                disabled={actionDisabled}
                options={actionOptions}
                onSelect={onActionSelect}
              />
            )}
            <Button
              variant="outlined"
              className="pd-run-action-btn"
              color="primary"
              startIcon={<HealingIcon />}
            >
              Heal failed tests
            </Button>
            <Button
              variant="outlined"
              color="primary"
              className="pd-run-action-btn"
              startIcon={<CallMergeIcon />}
            >
              Deduplicate tests
            </Button>
          </Box>
        )}
      </Box>

      <Box className="pd-metrics-grid pd-metrics-grid-summary">
        {currentVersion && (
          <Box className="pd-metric-card pd-metric-card-summary">
            <Typography component="span" className="pd-metric-label">
              Last run
            </Typography>
            <Typography component="span" className="pd-metric-value">
              {currentVersion.startedAt?.toLocaleDateString()}
            </Typography>
          </Box>
        )}
        <Box
          className="pd-metric-card"
          style={{ flex: '1 1 calc(33% - 8px)', minWidth: 100 }}
        >
          <Typography component="span" className="pd-metric-label">
            Test cases
          </Typography>
          <Typography component="span" className="pd-metric-value">
            {currentVersion?.testCases}
          </Typography>
        </Box>
        <Box
          className="pd-metric-card"
          style={{ flex: '1 1 calc(33% - 8px)', minWidth: 100 }}
        >
          <Typography component="span" className="pd-metric-label">
            Pass rate
          </Typography>
          <Typography
            component="span"
            className="pd-metric-value pd-metric-passed"
          >
            {currentVersion?.passRate}%
          </Typography>
        </Box>
        <Box
          className="pd-metric-card"
          style={{ flex: '1 1 calc(33% - 8px)', minWidth: 100 }}
        >
          <Typography component="span" className="pd-metric-label">
            Passed
          </Typography>
          <Typography
            component="span"
            className="pd-metric-value pd-metric-passed"
          >
            {currentVersion?.passed ?? '__'}
          </Typography>
        </Box>
        <Box
          className="pd-metric-card"
          style={{ flex: '1 1 calc(33% - 8px)', minWidth: 100 }}
        >
          <Typography component="span" className="pd-metric-label">
            Failed
          </Typography>
          <Typography
            component="span"
            className="pd-metric-value pd-metric-failed"
          >
            {currentVersion?.failed ?? '__'}
          </Typography>
        </Box>
        <Box
          className="pd-metric-card"
          style={{ flex: '1 1 calc(33% - 8px)', minWidth: 100 }}
        >
          <Typography component="span" className="pd-metric-label">
            Skipped
          </Typography>
          <Typography
            component="span"
            className="pd-metric-value pd-metric-failed"
          >
            {currentVersion?.skipped ?? '__'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
